import { DEFAULT_FORM_FONT_SIZE, FormFontSize, normalizeFormFontSize } from "./ingestion.form.font-size";

/** Parsed `ingestion_point.form_style` JSON for form-level appearance. */
export interface FormStyleConfig {
    headerImageKey?: string;
    titleFontSize: FormFontSize;
    titleColor?: string;
}

const DEFAULT_CONFIG: FormStyleConfig = {
    titleFontSize: DEFAULT_FORM_FONT_SIZE,
};

function normalizeOptionalString(raw: unknown): string | undefined {
    if (raw == null || typeof raw !== "string") {
        return undefined;
    }
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : undefined;
}

function parseFormStyleObject(formStyle: string | null | undefined): Record<string, unknown> {
    if (formStyle == null || String(formStyle).trim() === "") {
        return {};
    }
    try {
        const parsed = JSON.parse(formStyle) as unknown;
        return parsed != null && typeof parsed === "object" && !Array.isArray(parsed)
            ? (parsed as Record<string, unknown>)
            : {};
    } catch {
        return {};
    }
}

export function parseFormStyleConfig(formStyle: string | null | undefined): FormStyleConfig {
    if (formStyle == null || String(formStyle).trim() === "") {
        return { ...DEFAULT_CONFIG };
    }
    try {
        const raw = JSON.parse(formStyle) as Record<string, unknown>;
        const parsed: FormStyleConfig = {
            titleFontSize: normalizeFormFontSize(raw.titleFontSize),
        };
        const headerImageKey = normalizeOptionalString(raw.headerImageKey);
        if (headerImageKey) {
            parsed.headerImageKey = headerImageKey;
        }
        const titleColor = normalizeOptionalString(raw.titleColor);
        if (titleColor) {
            parsed.titleColor = titleColor;
        }
        return parsed;
    } catch {
        return { ...DEFAULT_CONFIG };
    }
}

export function stringifyFormStyleConfig(config: FormStyleConfig): string {
    const payload: Record<string, unknown> = {};

    if (config.headerImageKey) {
        payload.headerImageKey = config.headerImageKey;
    }

    if (config.titleFontSize !== DEFAULT_FORM_FONT_SIZE) {
        payload.titleFontSize = config.titleFontSize;
    }

    if (config.titleColor) {
        payload.titleColor = config.titleColor;
    }

    return JSON.stringify(payload);
}

/** Copy title styling but omit header image (FileStore keys are per ingestionPointId). */
export function copyFormStyleWithoutHeaderImage(formStyle: string | null | undefined): string | undefined {
    const parsed = parseFormStyleConfig(formStyle);
    const copied: FormStyleConfig = {
        titleFontSize: parsed.titleFontSize,
        titleColor: parsed.titleColor,
    };
    const serialized = stringifyFormStyleConfig(copied);
    return serialized === "{}" ? undefined : serialized;
}
